/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 * @description AddressPageContext
 */

import type { BoxProps, ImageProps } from '@chakra-ui/react'
import type { ButtonProps } from '@zcode-ui/controls'
import type { BuildingListingIdParams, NavBarBottomOrigin } from '@zcode-ui/web-zumper-features'
import type { AutocompleteResult, Media } from '@zcode/bizos'
import { type EVENT_NAMES, PROP_NAMES } from '@zcode/mixpanel'
import type { Origin } from '@zcode/nr-utils'
import type {
  FiltersState,
  FloorplanListing,
  Listing,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import type { Location, LocationDescriptor } from 'history'
import { createContext, useContext } from 'react'
import type { RouteComponentProps } from 'react-router-dom'
import type { Action } from 'redux-actions'
import invariant from 'tiny-invariant'

type APCOrigin =
  | NavBarBottomOrigin
  | typeof Origin.DETAIL_TOP_RIGHT_RAIL
  | typeof Origin.ADDR_DETAIL_TOP_RIGHT_RAIL
  | typeof Origin.DETAIL_TOP_RIGHT_TOUR
  | typeof Origin.ADDR_DETAIL_TOP_RIGHT_TOUR
  | typeof Origin.DETAIL_MOBILE_STICKY
  | typeof Origin.ADDR_DETAIL_MOBILE_STICKY
  | typeof Origin.DETAIL_MOBILE_STICKY_TOUR
  | typeof Origin.ADDR_DETAIL_MOBILE_STICKY_TOUR

type RemovePrefix<T extends string, Prefix extends string> = T extends `${Prefix}${infer Rest}`
  ? Rest
  : T
type LooseListingUId =
  | Listing['uId']
  | `l-${number}b`
  | RemovePrefix<Listing['uId'] | `l-${number}b`, 'l-'>

type DetailAdsLayoutTypeUnion =
  | {
      layout: 'square' | 'inactiveSquare' | 'topSquare'
      adType?: 1 | 2
    }
  | {
      layout: 'gallerySquare' | 'gallerySticky' | 'inactiveSticky' | 'leaderboard' | 'twoSquare'
      adType?: undefined
    }
export interface SelectListingIdProps {
  hashOnly?: boolean
  searchOnly?: boolean
  routeProps?: RouteComponentProps<{ addressId?: string }>
}

export interface AddressPageContextType {
  selectDisplayMedia: (
    entity: Pick<Property, 'media'> | null | undefined
  ) => readonly Media[] | null
  DetailAds: React.FunctionComponent<
    Pick<BoxProps, 'marginBottom' | 'marginTop'> & {
      showBorderBottom?: boolean
      showBorderTop?: boolean
    } & DetailAdsLayoutTypeUnion
  >
  DetailAppBanner: React.FunctionComponent<{}>
  FavoriteContainer: React.FC<{
    className?: string
    entity: Property | null | undefined
    isRoundButton?: boolean
    size?: number
    stroke?: string
    borderStyle?: 'none' | 'solid'
  }>
  getRootRelativeUrlFromFiltersAndLocation: (args: {
    bubbleId?: string | undefined
    currentUrl: string
    filters: FiltersState
    locationUrlQueries?: string[] | undefined
    omitBox?: boolean | undefined
  }) => string
  getImagePlaceholder: (groupId: number) => string
  handleBackBtnClick: (result: AutocompleteResult) => void
  hoodAndCityLinks: ReadonlyArray<{ label: string; url: string }>
  keepBetaParam: <ToLocationState>(
    to: LocationDescriptor<ToLocationState>
  ) => (location: Location<unknown>) => LocationDescriptor<ToLocationState>
  mapBoxBrowserKey: string
  PropertyOverview: React.FC
  MessageButton: React.ForwardRefExoticComponent<
    React.RefAttributes<HTMLAnchorElement & HTMLButtonElement> &
      Pick<ButtonProps<undefined>, 'className' | 'size'> & {
        boxProps?: Omit<BoxProps, 'as'>
        equalCTAIntent?: 'Apply' | 'Message' | 'Waitlist'
        isFullWidth?: boolean
        origin: APCOrigin
      }
  >
  mixpanelEvent: (
    event: typeof EVENT_NAMES.CLICK,
    origin: undefined,
    extraPayload: { [PROP_NAMES.DETAIL_NAVIGATION_TAB]: string }
  ) => void
  Navbar: React.FC
  NotifyMeButton: React.FC<
    {
      applyTopBarStyle?: boolean
      origin:
        | typeof Origin.DETAIL_TOP_RIGHT_RAIL
        | typeof Origin.ADDR_DETAIL_TOP_RIGHT_RAIL
        | typeof Origin.DETAIL_NAV_STICKY
        | typeof Origin.ADDR_DETAIL_NAV_STICKY
        | typeof Origin.DETAIL_MOBILE_STICKY
        | typeof Origin.ADDR_DETAIL_MOBILE_STICKY
        | typeof Origin.FLOORPLAN
        | typeof Origin.MOBILE_FLOORPLAN
      selectedFloorplan?: FloorplanListing<NormalFloorplanListingData>
    } & ButtonProps<undefined>
  >
  InactiveSaveSearchSection: React.FC<
    {
      applyTopBarStyle?: boolean
      origin: typeof Origin.DETAIL_TOP_RIGHT_RAIL | typeof Origin.ADDR_DETAIL_TOP_RIGHT_RAIL
    } & BoxProps
  >
  preloadRoutes: (componentNames: string[]) => void
  pushSearch: import('redux-actions').ActionFunction1<
    {
      search?: string | undefined
    },
    Action<
      '@@zumper/history-middleware/PUSH_SEARCH',
      {
        search?: string | undefined
      }
    >
  >
  Routes: {
    CURRENT_SEARCH_ROUTE: string
  }
  selectIsMessaged: (
    entity: Pick<Property, 'buildingId' | 'listingId' | 'pbId' | 'plId'> | null | undefined
  ) => boolean
  selectListingId: (
    params:
      | (SelectListingIdProps & { hashOnly: true })
      | (SelectListingIdProps & { searchOnly: true })
      | SelectListingIdProps
  ) => LooseListingUId | string | undefined
  TourButton: React.FC<{
    buttonProps?: ButtonProps<undefined>
    imageProps?: ImageProps
    origin: APCOrigin
  }> & { defaultProps?: never; propTypes?: never }
  useStreamChannel: (props: BuildingListingIdParams) => {
    loading: boolean
    streamChannelId: string | null | undefined
  }
  UnitsTableContainer: React.FC<{
    forceInView?: boolean
    rootMargin?: string
  }>
}

export const AddressPageContext = createContext<AddressPageContextType | null>(null)

export const useAddressPageContext = () => {
  const context = useContext(AddressPageContext)
  invariant(context)
  return context
}

export const useIsNewAddressPage = () => {
  const context = useContext(AddressPageContext)
  return !!context
}
